import { useEffect, useRef, useState } from "react";

const products = [
  {
    id: "vpn",
    name: "VPN",
    tagline: "Secure. Private. Fast.",
    description:
      "Our VPN application delivers military-grade encryption directly to your iOS device. Browse the internet with complete anonymity, protect sensitive data on public networks, and maintain your digital privacy with zero-log policy and automatic kill-switch technology.",
    features: ["256-bit AES Encryption", "Zero-Log Policy", "Auto Kill-Switch", "Global Server Network"],
    label: "Security",
    icon: "ri-shield-keyhole-line",
    gradient: "linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%)",
    accentColor: "#4a9eff",
    image: "https://public.readdy.ai/ai/img_res/4c14544194dcd6096ce21a52cfe8755a.jpg",
  },
  {
    id: "cleaner",
    name: "Cleaner",
    tagline: "Optimize. Clean. Perform.",
    description:
      "Keep your iPhone running at peak performance with our intelligent Cleaner app. Automatically detect and remove junk files, duplicate photos, and unnecessary cache data. Free up storage space and boost your device speed with just a single tap.",
    features: ["Smart Junk Detection", "Duplicate Photo Finder", "Cache Cleaner", "Storage Analyzer"],
    label: "Optimization",
    icon: "ri-sparkling-2-line",
    gradient: "linear-gradient(135deg, #00b894 0%, #00a381 50%, #007a60 100%)",
    accentColor: "#7de8c8",
    image: "https://readdy.ai/api/search-image?query=abstract%20clean%20mint%20green%20teal%20digital%20optimization%20sparkle%20particles%20minimalist%20tech%20background%20with%20soft%20glowing%20geometric%20shapes%20on%20dark%20teal%20background&width=600&height=480&seq=cleaner001&orientation=landscape",
  },
];

function ProductCard({ product, index }: { product: typeof products[0]; index: number }) {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.15 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return (
    <div
      ref={ref}
      className="group relative rounded-3xl overflow-hidden cursor-default transition-all duration-500 hover:-translate-y-2"
      style={{
        background: product.gradient,
        opacity: visible ? 1 : 0,
        transform: visible ? "translateY(0)" : `translateY(40px)`,
        transition: `opacity 0.7s ease ${index * 0.15}s, transform 0.7s ease ${index * 0.15}s`,
        minHeight: 480,
      }}
    >
      {/* Background image overlay */}
      <div className="absolute inset-0 opacity-20">
        <img src={product.image} alt="" className="w-full h-full object-cover object-top" />
      </div>

      {/* Content */}
      <div className="relative z-10 p-10 md:p-12 flex flex-col h-full" style={{ minHeight: 480 }}>
        {/* Icon */}
        <div className="w-16 h-16 flex items-center justify-center rounded-2xl mb-8"
          style={{ background: "rgba(255,255,255,0.15)" }}>
          <i className={`${product.icon} text-white text-3xl`} />
        </div>

        {/* Name & tagline */}
        <h3 className="text-4xl font-bold text-white mb-2"
          style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
          {product.name}
        </h3>
        <p className="text-sm font-semibold tracking-widest uppercase mb-6"
          style={{ color: product.accentColor }}>
          {product.tagline}
        </p>

        {/* Description */}
        <p className="text-white/70 text-base leading-relaxed mb-8 flex-1">
          {product.description}
        </p>

        {/* Features list */}
        <ul className="flex flex-col gap-2 mb-8">
          {product.features.map((f) => (
            <li key={f} className="flex items-center gap-3 text-white/80 text-sm">
              <span className="w-4 h-4 flex items-center justify-center">
                <i className="ri-check-line text-xs" style={{ color: product.accentColor }} />
              </span>
              {f}
            </li>
          ))}
        </ul>

        {/* Label pill */}
        <div className="self-start">
          <span className="inline-block border border-white/30 text-white/80 text-xs font-semibold tracking-widest uppercase px-4 py-2 rounded-full">
            {product.label}
          </span>
        </div>
      </div>
    </div>
  );
}

export default function ProductsSection() {
  const titleRef = useRef<HTMLDivElement>(null);
  const [titleVisible, setTitleVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setTitleVisible(true); },
      { threshold: 0.2 }
    );
    if (titleRef.current) observer.observe(titleRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section id="products" className="py-28 md:py-36 bg-white">
      <div className="max-w-6xl mx-auto px-6 md:px-10">
        {/* Section header */}
        <div
          ref={titleRef}
          className="text-center mb-16 md:mb-20"
          style={{
            opacity: titleVisible ? 1 : 0,
            transform: titleVisible ? "translateY(0)" : "translateY(30px)",
            transition: "opacity 0.6s ease, transform 0.6s ease",
          }}
        >
          <span className="text-xs font-semibold tracking-widest uppercase text-gray-400 mb-4 block">
            What We Build
          </span>
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900"
            style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
            Our Products
          </h2>
          <p className="mt-4 text-gray-500 text-lg max-w-xl mx-auto leading-relaxed">
            Carefully crafted iOS applications designed to protect and optimize your mobile experience.
          </p>
        </div>

        {/* Products grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {products.map((product, i) => (
            <ProductCard key={product.id} product={product} index={i} />
          ))}
        </div>
      </div>
    </section>
  );
}
